import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router';

export interface ConfirmLeaveContextProps {
    register(blockInAppNavigation: boolean): () => void;
}

const noop = () => undefined;
const context = React.createContext<ConfirmLeaveContextProps>({ register: () => noop });

interface ProviderProps {
    children: ReactNode;
}

interface RegistrationCounts {
    unload: number;
    navigation: number;
}

interface NavigationBlockerProps {
    message: string;
}

const NavigationBlocker = ({ message }: NavigationBlockerProps) => {
    const blocker = useBlocker(
        ({ currentLocation, nextLocation }) => currentLocation.pathname !== nextLocation.pathname,
    );

    useEffect(() => {
        if (blocker.state !== 'blocked') {
            return;
        }

        if (window.confirm(message)) {
            blocker.proceed();
        } else {
            blocker.reset();
        }
    }, [blocker, message]);

    return null;
};

export function ConfirmLeaveProvider({ children }: ProviderProps) {
    const { t } = useTranslation();
    const [counts, setCounts] = useState<RegistrationCounts>({ unload: 0, navigation: 0 });
    const message = t('client.editableDetails.discardChangesConfirmationMessage');

    const register = useCallback((blockInAppNavigation: boolean) => {
        setCounts((current) => ({
            unload: current.unload + 1,
            navigation: current.navigation + (blockInAppNavigation ? 1 : 0),
        }));

        return () => {
            setCounts((current) => ({
                unload: current.unload - 1,
                navigation: current.navigation - (blockInAppNavigation ? 1 : 0),
            }));
        };
    }, []);

    useEffect(() => {
        if (counts.unload === 0) {
            return;
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = message;
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [counts.unload, message]);

    // useMemo required - context value passed to provider.
    // React Compiler doesn't optimize context values automatically.
    const contextValue = useMemo(() => ({ register }), [register]);

    return (
        <context.Provider value={contextValue}>
            {counts.navigation > 0 && <NavigationBlocker message={message} />}
            {children}
        </context.Provider>
    );
}

interface ConfirmLeaveOptions {
    blockInAppNavigation?: boolean;
}

export const useConfirmLeave = (
    editing: boolean,
    { blockInAppNavigation = true }: ConfirmLeaveOptions = {},
) => {
    const { register } = useContext(context);

    useEffect(() => {
        if (!editing) {
            return;
        }

        return register(blockInAppNavigation);
    }, [blockInAppNavigation, editing, register]);
};
